import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import {
  Avatar,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BetOutcomeIcon from "../../Mobile/components/BetOutcomeIcon";
import LeaguesDropdown, {
  LeagueDescription,
} from "../../Mobile/components/LeaguesDropdown";
import { getMatchBetBreakdown } from "../../services/betService";
import { League } from "../../types/League";
import { MatchBetBreakdown, MatchBetBreakdownRow } from "../../types/MatchBetBreakdown";
import { getMatchBettingState } from "../../utils/betting";
import { getDateFromDate, getLocalTimeFromDate } from "../../utils/timeUtils";
import { useFixtures } from "../contexts/fixturesContext";
import { useUser } from "../contexts/userContext";
import "./MatchBetBreakdownPage.scss";

interface MatchBetBreakdownPageProps {
  isDesktop?: boolean;
}

export interface ScoreCombinationSummary {
  homeScore: number;
  awayScore: number;
  count: number;
}

export function sortBreakdownRows(
  rows: MatchBetBreakdownRow[]
): MatchBetBreakdownRow[] {
  return [...rows].sort((a, b) => {
    const pointsA = a.points ?? (a.hasBet ? 0 : -1);
    const pointsB = b.points ?? (b.hasBet ? 0 : -1);

    if (pointsA !== pointsB) {
      return pointsB - pointsA;
    }

    return a.username.localeCompare(b.username);
  });
}

export function buildScoreCombinationSummary(
  rows: MatchBetBreakdownRow[]
): ScoreCombinationSummary[] {
  const grouped = new Map<string, ScoreCombinationSummary>();

  rows.forEach((row) => {
    if (
      !row.hasBet ||
      row.homeTeamScoreBet === null ||
      row.awayTeamScoreBet === null
    ) {
      return;
    }

    const key = `${row.homeTeamScoreBet}:${row.awayTeamScoreBet}`;
    const existing = grouped.get(key);

    if (existing) {
      existing.count += 1;
      return;
    }

    grouped.set(key, {
      homeScore: row.homeTeamScoreBet,
      awayScore: row.awayTeamScoreBet,
      count: 1,
    });
  });

  return Array.from(grouped.values()).sort(
    (a, b) =>
      b.count - a.count ||
      a.homeScore - b.homeScore ||
      a.awayScore - b.awayScore
  );
}

export function buildTeamsWithExactResultLabel(
  homeTeamName: string,
  awayTeamName: string,
  isMatchPlayed: boolean,
  homeScoreRegular: number,
  awayScoreRegular: number
): string {
  const teamsLabel = `${homeTeamName} vs ${awayTeamName}`;

  if (!isMatchPlayed) {
    return teamsLabel;
  }

  return `${teamsLabel} (${homeScoreRegular}-${awayScoreRegular})`;
}

const MatchBetBreakdownPage: React.FC<MatchBetBreakdownPageProps> = ({
  isDesktop,
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { matchId } = useParams();
  const { matches } = useFixtures();
  const { user } = useUser();

  const parsedMatchId = Number(matchId);
  const currentMatch = matches.find((item) => item.id === parsedMatchId);

  const leagueOptions = useMemo(
    () =>
      (user.leagues ?? []).map((league: League) => ({
        label: league.name,
        leagueId: league.id,
      })),
    [user.leagues]
  );

  const [selectedLeague, setSelectedLeague] = useState<LeagueDescription | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<MatchBetBreakdown | null>(null);

  const handleLeagueChange = (league: LeagueDescription) => {
    setSelectedLeague(league);
  };

  useEffect(() => {
    if (leagueOptions.length === 0 || selectedLeague !== null) {
      return;
    }

    const preferredLeagueId =
      typeof state?.leagueId === "number" ? state.leagueId : undefined;

    const initialLeague =
      leagueOptions.find((option) => option.leagueId === preferredLeagueId) ??
      leagueOptions[0];

    setSelectedLeague(initialLeague);
  }, [leagueOptions, selectedLeague, state]);

  useEffect(() => {
    if (!Number.isFinite(parsedMatchId) || !selectedLeague) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    getMatchBetBreakdown(parsedMatchId, selectedLeague.leagueId)
      .then((response) => {
        if (!cancelled) {
          setData(response);
        }
      })
      .catch((requestError: unknown) => {
        if (!cancelled) {
          const isForbiddenError =
            requestError instanceof Error && requestError.message.includes("status 403");

          setData(null);
          setError(
            isForbiddenError
              ? "Bets by users are visible only after the match is completed."
              : "Unable to load match bets for this league."
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [parsedMatchId, selectedLeague]);

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    return sortBreakdownRows(data.rows);
  }, [data]);

  const usersWithBetCount = sortedRows.filter((row) => row.hasBet).length;
  const scoreCombinations = useMemo<ScoreCombinationSummary[]>(() => {
    if (!data?.rows) {
      return [];
    }
    return buildScoreCombinationSummary(data.rows);
  }, [data]);
  const shouldShowSummary = scoreCombinations.length > 0;

  const bettingState = getMatchBettingState(currentMatch);
  const allMissing = sortedRows.length > 0 && usersWithBetCount === 0;
  const partial = usersWithBetCount > 0 && usersWithBetCount < sortedRows.length;
  const fullyClosed =
    !!data &&
    !data.isBettingOpen &&
    usersWithBetCount === sortedRows.length &&
    sortedRows.length > 0;

  return (
    <div
      className={`matchBreakdown${isDesktop ? " matchBreakdown--desktop" : ""}`}
    >
      <div className="matchBreakdown__shell">
        <div className="matchBreakdown__header">
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            className="matchBreakdown__back"
          >
            Back
          </Button>

          <Typography className="matchBreakdown__title">
            Match Bet Breakdown
          </Typography>

          {currentMatch && (
            <Typography className="matchBreakdown__subtitle">
              {buildTeamsWithExactResultLabel(
                currentMatch.homeTeam.internationalName,
                currentMatch.awayTeam.internationalName,
                currentMatch.wasPlayed,
                currentMatch.homeScoreRegular,
                currentMatch.awayScoreRegular
              )}
            </Typography>
          )}

          {currentMatch && (
            <div className="matchBreakdown__kickoff">
              <ScheduleRoundedIcon />
              <span>
                {getDateFromDate(currentMatch.kickOffTime?.dateTime || "")} {" "}
                {getLocalTimeFromDate(currentMatch.kickOffTime?.dateTime || "")}
              </span>
            </div>
          )}

          <div className="matchBreakdown__filters">
            <LeaguesDropdown
              setCurrentLeague={handleLeagueChange}
              leagueOptions={leagueOptions}
              currentLeague={selectedLeague?.label}
              width="100%"
            />
          </div>

          <div className="matchBreakdown__statusRow">
            <StatusPill
              label={
                bettingState === "open"
                  ? "Betting open"
                  : bettingState === "locked"
                  ? "Betting closed"
                  : "Match already played"
              }
              tone={
                bettingState === "open"
                  ? "open"
                  : bettingState === "locked"
                  ? "locked"
                  : "played"
              }
            />
            {data && (
              <StatusPill
                label={`${usersWithBetCount}/${sortedRows.length} users submitted`}
                tone="default"
              />
            )}
            {allMissing && <StatusPill label="No bets yet" tone="locked" />}
            {partial && <StatusPill label="Partial bets" tone="open" />}
            {fullyClosed && <StatusPill label="Fully closed betting" tone="played" />}
          </div>

        </div>

        {loading ? (
          <div className="matchBreakdown__state">
            <CircularProgress />
          </div>
        ) : error ? (
          <div className="matchBreakdown__state">
            <Typography color="error">{error}</Typography>
          </div>
        ) : !selectedLeague ? (
          <div className="matchBreakdown__state">
            <Typography>No leagues available for your account.</Typography>
          </div>
        ) : (
          <div className="matchBreakdown__content">
            {shouldShowSummary && (
              <div className="matchBreakdown__summary">
                <Typography className="matchBreakdown__summaryTitle">
                  Popular bet scores
                </Typography>

                <div className="matchBreakdown__summaryList">
                  {scoreCombinations.map((item) => {
                    const scoreLabel = `${item.homeScore}:${item.awayScore}`;
                    const countLabel = `${item.count} ${item.count === 1 ? "bet" : "bets"}`;

                    return (
                      <div className="matchBreakdown__summaryRow" key={scoreLabel}>
                        <span className="matchBreakdown__summaryScore">{scoreLabel}</span>
                        <span className="matchBreakdown__summaryArrow">-&gt;</span>
                        <span className="matchBreakdown__summaryCount">{countLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="matchBreakdown__list">
              {sortedRows.map((row) => (
                <BreakdownRow key={row.userId} row={row} />
              ))}
              {sortedRows.length === 0 && (
                <div className="matchBreakdown__state">
                  <Typography>No users found in this league.</Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface BreakdownRowProps {
  row: MatchBetBreakdownRow;
}

const BreakdownRow: React.FC<BreakdownRowProps> = ({ row }) => {
  return (
    <div className="matchBreakdown__row">
      <div className="matchBreakdown__user">
        <Avatar src={row.avatarUrl} className="matchBreakdown__avatar">
          {row.username?.charAt(0)}
        </Avatar>
        <span className="matchBreakdown__username">{row.username}</span>
      </div>

      {row.hasBet ? (
        <div className="matchBreakdown__bet">
          <span className="matchBreakdown__score">
            {row.homeTeamScoreBet}-{row.awayTeamScoreBet}
          </span>
          {row.betResult !== null && row.betResult !== undefined && (
            <span className="matchBreakdown__result">
              <BetOutcomeIcon outcome={row.betResult} />
              <span>{row.points ?? 0} pts</span>
            </span>
          )}
        </div>
      ) : (
        <span className="matchBreakdown__missing">No bet submitted</span>
      )}
    </div>
  );
};

interface StatusPillProps {
  label: string;
  tone: "default" | "open" | "locked" | "played";
}

const StatusPill: React.FC<StatusPillProps> = ({ label, tone }) => (
  <span className={`matchBreakdown__pill matchBreakdown__pill--${tone}`}>{label}</span>
);

export default MatchBetBreakdownPage;
